<template>
  <component
    :is="modalComponent.component"
    v-model="internalValue"
    :size="modalComponent.size"
    v-bind="$attrs"
    @on-close="onClose"
  >
    <template #header>
      <slot name="header" />
    </template>

    <slot />

    <template #close>
      <slot name="close" />
    </template>
  </component>
  <div v-if="isMobile && internalValue" class="overlay"></div>
</template>

<script lang="ts" setup>
  import { SvoiCurtainModal, SvoiModal } from '@svoi-ui/components/modal'
  import { ModalSizes } from '@svoi-ui/interfaces/modal'
  import { useScrollLock } from '@vueuse/core'

  interface Props {
    modelValue?: boolean
    size?: ModalSizes
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: false,
    size: undefined,
  })

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void
    (e: 'on-close'): void
  }>()

  const { isMobile } = useBreakpoint()
  const isLocked = useScrollLock(document.body)

  const internalValue = computed({
    get: () => props.modelValue,
    set: (value: boolean) => {
      emit('update:modelValue', value)
    },
  })

  const modalComponent = computed(() => {
    if (isMobile.value) {
      return { component: SvoiCurtainModal, size: props.size ?? ModalSizes.full }
    }

    return { component: SvoiModal, size: props.size ?? ModalSizes.sm }
  })

  const onClose = () => {
    emit('on-close')
    internalValue.value = false
  }

  // Управление блокировкой скролла через watch
  watch(
    () => props.modelValue,
    newValue => {
      isLocked.value = newValue
    },
    { immediate: true }
  )

  // Обработка жизненного цикла с keep-alive
  onActivated(() => {
    if (props.modelValue) {
      isLocked.value = true
    }
  })

  onDeactivated(() => {
    isLocked.value = false
  })

  // Обработка размонтирования
  onUnmounted(() => {
    isLocked.value = false
  })
</script>

<style>
  .overlay {
    z-index: 999;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgb(0 0 0 / 35%);
  }
</style>
